/**
 * API service for Commodity Trade Modification endpoints (KAN-307).
 */

import { apiClient, APIResponse } from './client';

// ---------------------------------------------------------------------------
// Request / Response types
// ---------------------------------------------------------------------------

export interface CommodityModifyDirectPayload {
  modifiedFields: Record<string, any>;
  comment: string;
}

export interface CommodityCancelDirectPayload {
  cancellationReason: string;
  comment: string;
}

export interface CommodityInitiatePayload {
  action: 'modify' | 'cancel';
  comment: string;
}

export interface CommodityConfirmPayload {
  modifiedFields?: Record<string, any>;
  cancellationReason?: string;
}

export interface CommodityRejectPayload {
  reason: string;
}

export interface CommodityModificationResult {
  tradeId: string;
  tradeNumber: string;
  action: string;
  status: string;
  modifiedFields?: string[];
  quantityRecalculated?: boolean;
  childrenUpdated?: number;
  childrenCancelled?: number;
  editedInPlace?: boolean;
}

// ---------------------------------------------------------------------------
// Service class
// ---------------------------------------------------------------------------

class CommodityModificationService {
  private basePath = '/api/v1/commodity-modifications';

  /** List trades eligible for modification (Confirmed, parent-only). */
  async getEligibleTrades(clientId: string): Promise<APIResponse<any[]>> {
    return apiClient.get<any[]>(
      `${this.basePath}/${clientId}/eligible-trades`
    );
  }

  /** List trades with pending modification/cancellation requests. */
  async getPendingRequests(clientId: string): Promise<APIResponse<any[]>> {
    return apiClient.get<any[]>(
      `${this.basePath}/${clientId}/pending`
    );
  }

  /** List completed and rejected modifications/cancellations (history). */
  async getHistory(clientId: string): Promise<APIResponse<any[]>> {
    return apiClient.get<any[]>(
      `${this.basePath}/${clientId}/history`
    );
  }

  /** Single-step modify (self-approve users). */
  async modifyDirect(
    clientId: string,
    tradeId: string,
    payload: CommodityModifyDirectPayload,
    entityId: string
  ): Promise<APIResponse<CommodityModificationResult>> {
    return apiClient.post<CommodityModificationResult>(
      `${this.basePath}/${clientId}/trades/${tradeId}/modify-direct?entity_id=${entityId}`,
      payload
    );
  }

  /** Single-step cancel (self-approve users). */
  async cancelDirect(
    clientId: string,
    tradeId: string,
    payload: CommodityCancelDirectPayload,
    entityId: string
  ): Promise<APIResponse<CommodityModificationResult>> {
    return apiClient.post<CommodityModificationResult>(
      `${this.basePath}/${clientId}/trades/${tradeId}/cancel-direct?entity_id=${entityId}`,
      payload
    );
  }

  /** Initiate modification/cancellation (two-step, step 1). */
  async initiate(
    clientId: string,
    tradeId: string,
    payload: CommodityInitiatePayload,
    entityId: string
  ): Promise<APIResponse<CommodityModificationResult>> {
    return apiClient.post<CommodityModificationResult>(
      `${this.basePath}/${clientId}/trades/${tradeId}/initiate?entity_id=${entityId}`,
      payload
    );
  }

  /** Confirm pending modification/cancellation (two-step, step 2). */
  async confirm(
    clientId: string,
    tradeId: string,
    payload: CommodityConfirmPayload,
    entityId: string
  ): Promise<APIResponse<CommodityModificationResult>> {
    return apiClient.post<CommodityModificationResult>(
      `${this.basePath}/${clientId}/trades/${tradeId}/confirm?entity_id=${entityId}`,
      payload
    );
  }

  /** Reject pending modification/cancellation. */
  async reject(
    clientId: string,
    tradeId: string,
    payload: CommodityRejectPayload,
    entityId: string
  ): Promise<APIResponse<CommodityModificationResult>> {
    return apiClient.post<CommodityModificationResult>(
      `${this.basePath}/${clientId}/trades/${tradeId}/reject?entity_id=${entityId}`,
      payload
    );
  }
}

export const commodityModificationService = new CommodityModificationService();
